/* ============================================================
   TCG VAULT — Interactive Parallax Hero
   Vanilla JS. Shared by standalone + Shopify section.
   - Canvas view: zoom into the 1672x941 room montage so only a
     PART is visible, then PAN in X and Y with slow inertial cursor
     following (cinematic look-around feel).
   - Per-layer depth parallax via the perspective camera plus tiny
     per-layer drift for a flatter-but-believable fake 3D room.
   - Drawer settle-state (float + shine after card rise).
   - Touch: one-finger look-around with optional orientation input;
     fixed portrait/landscape framing; reduced-motion safe.
   ============================================================ */
(function () {
  "use strict";

  if (window.__tcgvVaultBoot) {
    window.__tcgvVaultBoot(document);
    return;
  }

  function initVault(root) {
    if (!root || root.__tcgvInit) return;
    root.__tcgvInit = true;

    var STAGE_W = 1672, STAGE_H = 941;
    var viewport = root.querySelector(".tcgv__viewport");
    var stage = root.querySelector(".tcgv__stage");
    var camera = root.querySelector(".tcgv__camera");
    var roomRig = root.querySelector(".tcgv-room-parts");
    if (!viewport || !stage || !camera) return;

    var reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    var coarseMQ = window.matchMedia("(pointer: coarse)");
    var mobileViewMQ = window.matchMedia("(max-width: 820px)");
    function isMobileCameraMode() {
      return coarseMQ.matches || mobileViewMQ.matches;
    }
    function isLandscapeViewport() {
      var width = viewport.clientWidth || window.innerWidth || 0;
      var height = viewport.clientHeight || window.innerHeight || 0;
      return width > height;
    }

    /* ---- tuning -------------------------------------------- */
    // How far we zoom past plain cover-fit. >1 means only a PART of
    // the room shows and there's room to pan and reveal the rest.
    var CANVAS_ZOOM = 1.22;
    // Fraction of the available slack we actually travel, kept under
    // 1 so the perspective tilt at the corners never bares an edge.
    var PAN_SAFE = 0.72;
    var YAW_MAX = 4.35;      // deg rotateY at the horizontal extremes
    var PITCH_MAX = 2.85;    // deg rotateX at the vertical extremes
    var ROLL_MAX = 0.34;     // very small shoulder/head drift
    var ORIGIN_X_MAX = 8.6;  // perspective-origin drift, in %
    var ORIGIN_Y_MAX = 6.4;
    var LOOK_EASE = 0.038;   // lower = heavier, smoother camera
    var MOBILE_PORTRAIT_LOOK_EASE = 0.085;
    var MOBILE_LANDSCAPE_LOOK_EASE = 0.12;
    var MOBILE_TILT_X_RANGE = 34;
    var MOBILE_TILT_Y_RANGE = 38;
    var MOBILE_TILT_LIMIT = 0.64;
    var MOBILE_TILT_LEFT_GAIN = 1.24;
    var MOBILE_LANDSCAPE_TILT_X_RANGE = 16;
    var MOBILE_LANDSCAPE_TILT_Y_RANGE = 18;
    var MOBILE_LANDSCAPE_TILT_LIMIT = 1;
    var MOBILE_PORTRAIT_FIT_WIDTH = 490;
    var TOUCH_TABLET_PORTRAIT_MAX_SCALE = 1.2;
    var MOBILE_LANDSCAPE_FIXED_ZOOM = 2;
    var LARGE_PORTRAIT_MIN_WIDTH = 900;
    var LARGE_PORTRAIT_MIN_HEIGHT = 1500;
    var LARGE_PORTRAIT_ENV_OVERSCAN = 1.04;
    var LARGE_PORTRAIT_PULLBACK = 0.72;
    var GYROSCOPE_ENABLED = false;

    function isLargePortraitViewport(width, height) {
      return width >= LARGE_PORTRAIT_MIN_WIDTH &&
        height >= LARGE_PORTRAIT_MIN_HEIGHT &&
        height > width;
    }

    /* ---- collect parallax layers (kept for reference) ------ */
    var layerNodes = root.querySelectorAll("[data-depth]");
    var layers = [];
    for (var i = 0; i < layerNodes.length; i++) {
      var node = layerNodes[i];
      var authoredDepth = parseFloat(node.getAttribute("data-depth")) || 1;
      // Keep the ground locked to the shelf seam; the camera tilt still gives
      // it perspective, but it no longer slides independently below the walls.
      if (node.classList && node.classList.contains("tcgv-room-part--ground")) {
        authoredDepth = 0.48;
      }
      layers.push({
        el: node,
        depth: authoredDepth
      });
    }
    var counterLayer = root.querySelector(".tcgv__counter");
    if (roomRig && counterLayer && !counterLayer.hasAttribute("data-depth")) {
      layers.push({ el: counterLayer, depth: 0.48 });
    }

    /* ---- fit: cover * canvas-zoom, compute pan slack -------- */
    // panX/panY are expressed in STAGE coordinates (the camera lives
    // inside the scaled stage), i.e. how many stage-px we can shove
    // the content sideways/vertically before an edge would show.
    var panX = 0, panY = 0;
    var landscapeContainActive = false;
    var landscapeViewportActive = window.innerWidth > window.innerHeight;

    function updateLandscapePanRange() {
      if (!landscapeContainActive) return;
      // A contained landscape room has deliberate black framing on either
      // side. At the fixed 2x scale, provide enough stable travel to inspect
      // the full room without exposing the stage edges.
      panX = STAGE_W * 0.24;
      panY = STAGE_H * 0.22;
    }

    function fit() {
      var vw = viewport.clientWidth || root.clientWidth;
      var vh = viewport.clientHeight || root.clientHeight;
      if (!vw || !vh) return;
      var largePortrait = isLargePortraitViewport(vw, vh);
      var mobileMode = isMobileCameraMode();
      var landscapeViewport = vw > vh;
      landscapeViewportActive = landscapeViewport;
      var landscapeContain = mobileMode && landscapeViewport;
      var cover = landscapeContain
        ? Math.min(vw / STAGE_W, vh / STAGE_H)
        : Math.max(vw / STAGE_W, vh / STAGE_H);
      var zoom = landscapeContain
        ? MOBILE_LANDSCAPE_FIXED_ZOOM
        : reduceMQ.matches ? 1 : CANVAS_ZOOM;
      var scale = cover * zoom;
      if (mobileViewMQ.matches && !landscapeViewport) {
        scale = Math.min(scale, vw / MOBILE_PORTRAIT_FIT_WIDTH);
      }
      if (coarseMQ.matches && !landscapeViewport && vw > 680) {
        scale = Math.min(scale, TOUCH_TABLET_PORTRAIT_MAX_SCALE);
      }
      if (largePortrait) {
        scale = vw / MOBILE_PORTRAIT_FIT_WIDTH;
      }
      root.classList.toggle("tcgv--large-portrait", largePortrait);
      if (largePortrait) {
        var environmentScale = Math.max(
          1,
          vh * LARGE_PORTRAIT_ENV_OVERSCAN /
            (STAGE_H * scale * LARGE_PORTRAIT_PULLBACK)
        );
        root.style.setProperty("--env-portrait-scale", environmentScale.toFixed(4));
      } else {
        root.style.removeProperty("--env-portrait-scale");
      }
      stage.style.setProperty("--fit-scale", scale.toFixed(4));
      pointerBounds = null;
      landscapeContainActive = landscapeContain;
      if (landscapeContain) {
        updateLandscapePanRange();
      } else {
        panX = Math.max(0, (STAGE_W - vw / scale) / 2) * PAN_SAFE;
        panY = Math.max(0, (STAGE_H - vh / scale) / 2) * PAN_SAFE;
      }
    }

    /* ---- camera state -------------------------------------- */
    var raf = null;
    var targetX = 0, targetY = 0;   // -1 .. 1 normalized cursor intent
    var curX = 0, curY = 0;         // eased
    var active = false;             // pointer present
    var pointerX = null, pointerY = null;
    var gyroListening = false;
    var gyroPermissionArmed = false;
    var gyroActive = false;
    var gyroEverReceived = false;
    var gyroWatchdogTimer = null;
    var gyroStaleTimer = null;
    var swipeListening = false;
    var swipeFallbackActive = false;
    var swipeTracking = false;
    var swipeLastX = 0, swipeLastY = 0;
    var swipeX = 0, swipeY = 0;
    var gyroBaseX = null, gyroBaseY = null, gyroScreenAngle = null;
    var gyroCalibrationX = 0, gyroCalibrationY = 0, gyroCalibrationSamples = 0;
    var gyroRecenterPending = false;
    var gyroX = 0, gyroY = 0, gyroLast = 0;
    var gyroLastSample = 0, queuedGyroX = 0, queuedGyroY = 0;
    var gyroStatus = "idle";
    var motionPrompt = null;
    var recenterControl = null;
    var motionHint = null;
    var motionHintHideTimer = null;
    var motionHintRecenterTimer = null;
    var motionHintReplay = 0;
    var orientationReplayTimer = null;
    var lastPaintX = 999;
    var lastPaintY = 999;
    var lastHitZoneSync = 0;
    var pointerBounds = null, pointerBoundsAt = 0;

    function getPointerBounds() {
      var now = Date.now();
      if (!pointerBounds || now - pointerBoundsAt > 120) {
        pointerBounds = viewport.getBoundingClientRect();
        if (!pointerBounds.width || !pointerBounds.height) pointerBounds = root.getBoundingClientRect();
        pointerBoundsAt = now;
      }
      return pointerBounds;
    }

    function axesFromPoint(clientX, clientY) {
      var rect = getPointerBounds();
      var nx = (clientX - rect.left) / rect.width;    // 0..1
      var ny = (clientY - rect.top) / rect.height;    // 0..1
      return {
        x: shapeAxis(Math.max(-1, Math.min(1, (nx - 0.5) * 2))),
        y: shapeAxis(Math.max(-1, Math.min(1, (ny - 0.5) * 2)))
      };
    }

    function setTarget(clientX, clientY) {
      var axes = axesFromPoint(clientX, clientY);
      targetX = axes.x;
      targetY = axes.y;
    }

    function shapeAxis(value) {
      var sign = value < 0 ? -1 : 1;
      var abs = Math.abs(value);
      var dead = 0.035;
      if (abs <= dead) return 0;
      var shaped = (abs - dead) / (1 - dead);
      return sign * Math.pow(shaped, 1.18);
    }

    function clampAxis(value) {
      return Math.max(-1, Math.min(1, value));
    }

    function screenAngle() {
      var legacyAngle = typeof window.orientation === "number" ? window.orientation : 0;
      if (window.screen && window.screen.orientation && typeof window.screen.orientation.angle === "number") {
        var reportedAngle = window.screen.orientation.angle;
        if (landscapeViewportActive && Math.abs(reportedAngle) % 180 !== 90 && Math.abs(legacyAngle) % 180 === 90) {
          return legacyAngle;
        }
        return reportedAngle;
      }
      return legacyAngle;
    }

    // Normalize native device axes to the visible screen before baselining.
    // Some Android browsers report a stale angle in landscape, so viewport
    // geometry is the source of truth for choosing the horizontal axis.
    function screenTilt(e, angle) {
      var beta = e.beta;
      var gamma = e.gamma;
      if (landscapeViewportActive) {
        if (angle === -90 || angle === 270) return { x: -beta, y: gamma };
        return { x: beta, y: -gamma };
      }
      if (Math.abs(angle) === 180) return { x: -gamma, y: -beta };
      return { x: gamma, y: beta };
    }

    function onDeviceOrientation(e) {
      if (!GYROSCOPE_ENABLED || reduceMQ.matches || !isMobileCameraMode()) return;
      if (typeof e.beta !== "number" || typeof e.gamma !== "number") return;
      gyroEverReceived = true;
      var wasSwipeFallback = swipeFallbackActive;
      clearTimeout(gyroWatchdogTimer);
      gyroWatchdogTimer = null;
      setSwipeFallback(false);
      if (gyroStatus !== "granted") {
        gyroStatus = "granted";
        hideMotionPrompt();
        showRecenterControl();
      }
      // A sensor may wake just after the availability watchdog. Replace the
      // temporary swipe guide immediately so working devices never keep it.
      if (wasSwipeFallback) showMotionHint();

      var angle = screenAngle();
      if (gyroScreenAngle !== angle) {
        var changingOrientation = gyroScreenAngle !== null;
        gyroBaseX = changingOrientation ? null : 0;
        gyroBaseY = changingOrientation ? null : 0;
        gyroRecenterPending = changingOrientation;
        gyroCalibrationX = 0;
        gyroCalibrationY = 0;
        gyroCalibrationSamples = 0;
        gyroX = 0;
        gyroY = 0;
        gyroLastSample = 0;
        gyroScreenAngle = angle;
      }
      var tilt = screenTilt(e, angle);

      if (gyroRecenterPending || gyroBaseX === null || gyroBaseY === null) {
        gyroCalibrationX += tilt.x;
        gyroCalibrationY += tilt.y;
        gyroCalibrationSamples += 1;
        if (gyroCalibrationSamples < 6) return;
        gyroBaseX = gyroCalibrationX / gyroCalibrationSamples;
        gyroBaseY = gyroCalibrationY / gyroCalibrationSamples;
        gyroRecenterPending = false;
        gyroCalibrationX = 0;
        gyroCalibrationY = 0;
        gyroCalibrationSamples = 0;
        gyroLastSample = 0;
        return;
      }

      var sampleNow = Date.now();
      if (sampleNow - gyroLastSample < 25) return;
      gyroLastSample = sampleNow;

      var landscape = landscapeViewportActive;
      var tiltXRange = landscape ? MOBILE_LANDSCAPE_TILT_X_RANGE : MOBILE_TILT_X_RANGE;
      var tiltYRange = landscape ? MOBILE_LANDSCAPE_TILT_Y_RANGE : MOBILE_TILT_Y_RANGE;
      var tiltLimit = landscape ? MOBILE_LANDSCAPE_TILT_LIMIT : MOBILE_TILT_LIMIT;
      var dx = (tilt.x - gyroBaseX) / tiltXRange;
      var dy = (tilt.y - gyroBaseY) / tiltYRange;

      var nextX = shapeAxis(clampAxis(dx)) * tiltLimit;
      var nextY = shapeAxis(clampAxis(dy)) * tiltLimit;
      if (nextX < 0) nextX = Math.max(-tiltLimit, nextX * MOBILE_TILT_LEFT_GAIN);
      if (Math.abs(nextX) < 0.035) nextX = 0;
      if (Math.abs(nextY) < 0.035) nextY = 0;
      gyroX += (nextX - gyroX) * 0.34;
      gyroY += (nextY - gyroY) * 0.34;
      if (Math.abs(gyroX) < 0.003) gyroX = 0;
      if (Math.abs(gyroY) < 0.003) gyroY = 0;
      var wasInactive = !gyroActive;
      gyroActive = true;
      gyroLast = sampleNow;
      scheduleGyroStaleCheck();
      if (
        wasInactive ||
        Math.abs(gyroX - queuedGyroX) > 0.008 ||
        Math.abs(gyroY - queuedGyroY) > 0.008
      ) {
        queuedGyroX = gyroX;
        queuedGyroY = gyroY;
        startLoop();
      }
    }

    function addGyroListener() {
      if (!GYROSCOPE_ENABLED || gyroListening || reduceMQ.matches || !isMobileCameraMode() || !("DeviceOrientationEvent" in window)) return;
      window.addEventListener("deviceorientation", onDeviceOrientation, { passive: true });
      gyroListening = true;
      clearTimeout(gyroWatchdogTimer);
      gyroWatchdogTimer = setTimeout(function () {
        if (!gyroEverReceived) setSwipeFallback(true);
      }, 2400);
    }

    function scheduleGyroStaleCheck() {
      // A working sensor can be throttled briefly by the browser. Once its
      // first valid reading arrives, retain gyro mode until a real reload.
      if (gyroStaleTimer) {
        clearTimeout(gyroStaleTimer);
        gyroStaleTimer = null;
      }
    }

    function setSwipeFallback(enabled) {
      enabled = !!enabled && isMobileCameraMode() && !reduceMQ.matches;
      if (swipeFallbackActive === enabled) return;
      swipeFallbackActive = enabled;
      root.classList.toggle("is-swipe-fallback", enabled);
      swipeTracking = false;
      if (enabled) {
        swipeX = curX;
        swipeY = curY;
        showMotionHint();
      }
      showRecenterControl();
      startLoop();
    }

    function onSwipeStart(event) {
      if (reduceMQ.matches || !isMobileCameraMode() || event.touches.length !== 1) {
        swipeTracking = false;
        return;
      }
      swipeTracking = true;
      swipeLastX = event.touches[0].clientX;
      swipeLastY = event.touches[0].clientY;
    }

    function onSwipeMove(event) {
      if (reduceMQ.matches || !isMobileCameraMode() || !swipeTracking || event.touches.length !== 1) return;
      var touch = event.touches[0];
      var width = Math.max(1, viewport.clientWidth);
      var height = Math.max(1, viewport.clientHeight);
      var landscapeSwipe = isLandscapeViewport();
      var horizontalGain = landscapeSwipe ? 4.4 : 1.7;
      var verticalGain = landscapeSwipe ? 3.2 : 1.7;
      var deltaLimit = landscapeSwipe ? 0.08 : 0.055;
      var deltaX = touch.clientX - swipeLastX;
      var deltaY = touch.clientY - swipeLastY;
      // Browsers occasionally coalesce touch events into a single large
      // delta. Cap that sample so a dropped event cannot jerk the camera.
      deltaX = Math.max(-width * deltaLimit, Math.min(width * deltaLimit, deltaX));
      deltaY = Math.max(-height * deltaLimit, Math.min(height * deltaLimit, deltaY));
      // Drag the camera across the room while preserving the selected view
      // after the finger lifts. Gyroscope movement, when available, remains
      // a small additive look offset around this swipe position.
      swipeX = clampAxis(swipeX - deltaX / width * horizontalGain);
      swipeY = clampAxis(swipeY - deltaY / height * verticalGain);
      swipeLastX = touch.clientX;
      swipeLastY = touch.clientY;
      startLoop();
      event.preventDefault();
    }

    function onSwipeEnd() {
      swipeTracking = false;
    }

    function enableSwipeLook() {
      if (swipeListening || !isMobileCameraMode()) return;
      viewport.addEventListener("touchstart", onSwipeStart, { passive: true });
      viewport.addEventListener("touchmove", onSwipeMove, { passive: false });
      viewport.addEventListener("touchend", onSwipeEnd, { passive: true });
      viewport.addEventListener("touchcancel", onSwipeEnd, { passive: true });
      swipeListening = true;
    }

    function installPageZoomGuard() {
      if (window.__tcgvPageZoomGuard) return;
      window.__tcgvPageZoomGuard = true;
      ["touchstart", "touchmove"].forEach(function (name) {
        document.addEventListener(name, function (event) {
          if (event.touches && event.touches.length > 1 && isMobileCameraMode()) {
            event.preventDefault();
          }
        }, { passive: false, capture: true });
      });
      ["gesturestart", "gesturechange", "gestureend"].forEach(function (name) {
        document.addEventListener(name, function (event) {
          if (isMobileCameraMode()) {
            event.preventDefault();
          }
        }, { passive: false });
      });
      window.addEventListener("wheel", function (event) {
        if (event.ctrlKey || event.metaKey) event.preventDefault();
      }, { passive: false });
      window.addEventListener("keydown", function (event) {
        if ((event.ctrlKey || event.metaKey) && (event.key === "+" || event.key === "-" || event.key === "=" || event.key === "0")) {
          event.preventDefault();
        }
      });
    }

    function requestGyroPermission() {
      if (!GYROSCOPE_ENABLED) {
        gyroStatus = "disabled";
        hideMotionPrompt();
        setSwipeFallback(true);
        return;
      }
      if (reduceMQ.matches || !isMobileCameraMode()) return;
      if (!("DeviceOrientationEvent" in window)) {
        gyroStatus = "unavailable";
        hideMotionPrompt();
        setSwipeFallback(true);
        return;
      }
      if (typeof window.DeviceOrientationEvent.requestPermission === "function") {
        gyroStatus = "prompting";
        window.DeviceOrientationEvent.requestPermission()
          .then(function (state) {
            if (state === "granted") {
              gyroStatus = "listening";
              hideMotionPrompt();
              resetGyroCenter();
              addGyroListener();
            } else {
              gyroStatus = "denied";
              hideMotionPrompt();
              setSwipeFallback(true);
            }
          })
          .catch(function () {
            gyroStatus = "denied";
            hideMotionPrompt();
            setSwipeFallback(true);
          });
      } else {
        gyroStatus = "listening";
        hideMotionPrompt();
        addGyroListener();
      }
    }

    function armGyro() {
      if (!GYROSCOPE_ENABLED) {
        gyroStatus = "disabled";
        hideMotionPrompt();
        setSwipeFallback(true);
        return;
      }
      if (reduceMQ.matches || !isMobileCameraMode()) return;
      if (!("DeviceOrientationEvent" in window)) {
        gyroStatus = "unavailable";
        hideMotionPrompt();
        setSwipeFallback(true);
        return;
      }
      if (typeof window.DeviceOrientationEvent.requestPermission === "function") {
        if (gyroPermissionArmed) return;
        gyroPermissionArmed = true;
        showMotionPrompt();
      } else {
        addGyroListener();
      }
    }

    function showMotionPrompt() {
      if (!GYROSCOPE_ENABLED || !isMobileCameraMode() || reduceMQ.matches || !("DeviceOrientationEvent" in window)) return;
      if (!motionPrompt) {
        motionPrompt = document.createElement("button");
        motionPrompt.type = "button";
        motionPrompt.className = "tcgv__motion-permission";
        motionPrompt.textContent = "Enable motion";
        motionPrompt.addEventListener("click", function (event) {
          event.preventDefault();
          requestGyroPermission();
        });
        root.appendChild(motionPrompt);
      }
      motionPrompt.classList.add("is-visible");
    }

    function hideMotionPrompt() {
      if (motionPrompt) motionPrompt.classList.remove("is-visible");
    }

    function showRecenterControl() {
      if (!isMobileCameraMode() || reduceMQ.matches) return;
      if (!recenterControl) {
        recenterControl = document.createElement("button");
        recenterControl.type = "button";
        recenterControl.className = "tcgv__motion-recenter";
        recenterControl.textContent = "Recenter view";
        recenterControl.addEventListener("click", function () {
          centerCamera(true);
        });
        root.appendChild(recenterControl);
      }
      recenterControl.classList.add("is-visible");
    }

    function hideRecenterControl() {
      if (recenterControl) recenterControl.classList.remove("is-visible");
    }

    function showMotionHint() {
      if (reduceMQ.matches || !isMobileCameraMode()) return;
      clearTimeout(motionHintHideTimer);
      clearTimeout(motionHintRecenterTimer);
      motionHintReplay += 1;
      var replay = motionHintReplay;
      if (!motionHint) {
        motionHint = document.createElement("div");
        motionHint.setAttribute("aria-hidden", "true");
        root.appendChild(motionHint);
      }
      motionHint.className = "tcgv__motion-hint is-swipe " + (landscapeViewportActive ? "is-landscape" : "is-portrait");
      motionHint.innerHTML = '<span class="tcgv__motion-hint-icon"><svg class="tcgv__swipe-hand" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 12V4.5a1.8 1.8 0 0 1 3.6 0V10"></path><path d="M12.6 9a1.8 1.8 0 0 1 3.6 0v2"></path><path d="M16.2 10a1.8 1.8 0 0 1 3.6 0v4.2a6.8 6.8 0 0 1-6.8 6.8h-.7a6 6 0 0 1-5-2.7l-3-4.5a1.8 1.8 0 0 1 2.8-2.2L9 13.2"></path></svg><span></span></span>' +
        '<span class="tcgv__motion-hint-copy"><b>Swipe to look around</b><small>Drag in any direction</small></span>';
      requestAnimationFrame(function () {
        if (replay === motionHintReplay) motionHint.classList.add("is-visible");
      });
      motionHintHideTimer = setTimeout(function () {
        if (replay === motionHintReplay) motionHint.classList.add("is-fading");
      }, 7000);
      motionHintRecenterTimer = setTimeout(function () {
        if (replay !== motionHintReplay || !recenterControl || !recenterControl.classList.contains("is-visible")) return;
        motionHint.className = "tcgv__motion-hint is-recenter";
        motionHint.innerHTML = '<span class="tcgv__recenter-pointer" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7l10 10"></path><path d="M17 8v9H8"></path></svg></span>' +
          '<span class="tcgv__motion-hint-copy"><b>View off-center?</b><small>Tap Recenter view</small></span>';
        requestAnimationFrame(function () {
          if (replay === motionHintReplay) motionHint.classList.add("is-visible");
        });
        motionHintHideTimer = setTimeout(function () {
          if (replay === motionHintReplay) motionHint.classList.add("is-fading");
        }, 4000);
      }, 9000);
    }

    function resetGyroCenter(recenterFromCurrentTilt) {
      gyroBaseX = recenterFromCurrentTilt ? null : 0;
      gyroBaseY = recenterFromCurrentTilt ? null : 0;
      gyroRecenterPending = !!recenterFromCurrentTilt;
      gyroCalibrationX = 0;
      gyroCalibrationY = 0;
      gyroCalibrationSamples = 0;
      // Preserve the current screen orientation while manually recentering;
      // otherwise the next sensor sample looks like a rotation and cancels it.
      gyroScreenAngle = recenterFromCurrentTilt ? screenAngle() : null;
      gyroActive = false;
      gyroX = 0;
      gyroY = 0;
      gyroLast = 0;
      gyroLastSample = 0;
      queuedGyroX = 0;
      queuedGyroY = 0;
    }

    function centerCamera(recenterFromCurrentTilt) {
      active = false;
      pointerX = null;
      pointerY = null;
      targetX = 0;
      targetY = 0;
      curX = 0;
      curY = 0;
      swipeX = 0;
      swipeY = 0;
      lastPaintX = 999;
      lastPaintY = 999;
      resetGyroCenter(!!recenterFromCurrentTilt);
      camera.style.setProperty("--cam-x", "0px");
      camera.style.setProperty("--cam-y", "0px");
      camera.style.setProperty("--cam-yaw", "0deg");
      camera.style.setProperty("--cam-pitch", "0deg");
      camera.style.setProperty("--cam-roll", "0deg");
      camera.style.setProperty("--scene-pan-x", "0px");
      stage.style.setProperty("--look-origin-x", "50%");
      stage.style.setProperty("--look-origin-y", "48%");
      for (var i = 0; i < layers.length; i++) {
        layers[i].el.style.setProperty("--layer-x", "0px");
        layers[i].el.style.setProperty("--layer-y", "0px");
        layers[i].el.style.setProperty("--layer-roll", "0deg");
      }
    }

    function onMove(e) {
      if (reduceMQ.matches) return;
      active = true;
      pointerX = e.clientX;
      pointerY = e.clientY;
      setTarget(e.clientX, e.clientY);
      startLoop();
    }
    function onLeave() {
      active = false;
      pointerX = null;
      pointerY = null;
      targetX = 0;
      targetY = 0;
      startLoop();
    }

    function frame() {
      var mobileMode = isMobileCameraMode();
      var mobileLandscape = mobileMode && isLandscapeViewport();
      // Before permission, after denial, or without a compatible sensor the
      // mobile room stays centered and fully usable.
      if (!reduceMQ.matches && mobileMode) {
        targetX = clampAxis(swipeX + (GYROSCOPE_ENABLED && gyroActive ? gyroX : 0));
        targetY = clampAxis(swipeY + (GYROSCOPE_ENABLED && gyroActive ? gyroY : 0));
        active = true;
      }

      var ease = mobileMode
        ? (mobileLandscape ? MOBILE_LANDSCAPE_LOOK_EASE : MOBILE_PORTRAIT_LOOK_EASE)
        : LOOK_EASE;
      curX += (targetX - curX) * ease;
      curY += (targetY - curY) * ease;
      if (Math.abs(targetX - curX) < 0.0006) curX = targetX;
      if (Math.abs(targetY - curY) < 0.0006) curY = targetY;
      var paintThreshold = mobileMode ? 0.0015 : 0.0009;
      var movedEnough = Math.abs(curX - lastPaintX) > paintThreshold || Math.abs(curY - lastPaintY) > paintThreshold;

      if (!movedEnough) {
        if (mobileMode) {
          if (Math.abs(targetX - curX) > 0.0015 || Math.abs(targetY - curY) > 0.0015) {
            raf = requestAnimationFrame(frame);
            return;
          }
          raf = null;
        } else {
          if (Math.abs(targetX - curX) > 0.0009 || Math.abs(targetY - curY) > 0.0009) {
            raf = requestAnimationFrame(frame);
          } else {
            if (root.__tcgvSyncHits) root.__tcgvSyncHits(true);
            raf = null;
          }
        }
        return;
      }
      lastPaintX = curX;
      lastPaintY = curY;

      // PAN: translate the canvas OPPOSITE the cursor so moving the
      // mouse right slides the room left and reveals its right side.
      var camX = -curX * panX;
      var camY = -curY * panY * 0.86;
      // subtle head-turn layered on top for depth
      var yaw = -curX * YAW_MAX;
      var pitch = curY * PITCH_MAX;     // look down -> tilt to reveal floor
      var roll = curX * ROLL_MAX;

      camera.style.setProperty("--cam-x", camX.toFixed(2) + "px");
      camera.style.setProperty("--cam-y", camY.toFixed(2) + "px");
      camera.style.setProperty("--cam-yaw", yaw.toFixed(3) + "deg");
      camera.style.setProperty("--cam-pitch", pitch.toFixed(3) + "deg");
      camera.style.setProperty("--cam-roll", roll.toFixed(3) + "deg");
      if (!mobileMode) {
        var originX = 50 + curX * ORIGIN_X_MAX;
        var originY = 48 + curY * ORIGIN_Y_MAX;
        stage.style.setProperty("--look-origin-x", originX.toFixed(2) + "%");
        stage.style.setProperty("--look-origin-y", originY.toFixed(2) + "%");
        for (var i = 0; i < layers.length; i++) {
          var layer = layers[i];
          // Depth is authored so lower values are farther and larger values
          // are nearer. Keep the room floor and central table anchored to the
          // shelf seam; camera tilt/Z-depth still provide the perspective.
          var near = Math.max(-0.22, Math.min(0.28, layer.depth - 0.62));
          var layerX = -curX * near * 48;
          var layerY = -curY * near * 30;
          var layerRoll = curX * near * 0.22;
          layer.el.style.setProperty("--layer-x", layerX.toFixed(2) + "px");
          layer.el.style.setProperty("--layer-y", layerY.toFixed(2) + "px");
          layer.el.style.setProperty("--layer-roll", layerRoll.toFixed(3) + "deg");
        }
      }

      if (mobileMode) {
        if (Math.abs(targetX - curX) > 0.0009 || Math.abs(targetY - curY) > 0.0009) {
          raf = requestAnimationFrame(frame);
        } else {
          raf = null;
        }
      } else {
        if (root.__tcgvSyncHits) root.__tcgvSyncHits();
        if (Math.abs(targetX - curX) > 0.0009 || Math.abs(targetY - curY) > 0.0009) {
          raf = requestAnimationFrame(frame);
        } else {
          raf = null;
        }
      }
    }

    function startLoop() { if (!raf) raf = requestAnimationFrame(frame); }
    function stopLoop() {
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      camera.style.setProperty("--cam-x", "0px");
      camera.style.setProperty("--cam-y", "0px");
      camera.style.setProperty("--cam-yaw", "0deg");
      camera.style.setProperty("--cam-pitch", "0deg");
      camera.style.setProperty("--cam-roll", "0deg");
      if (roomRig) {
        roomRig.style.setProperty("--room-x", "0px");
        roomRig.style.setProperty("--room-y", "0px");
        roomRig.style.setProperty("--room-yaw", "0deg");
        roomRig.style.setProperty("--room-pitch", "0deg");
        roomRig.style.setProperty("--room-roll", "0deg");
      }
      stage.style.setProperty("--look-origin-x", "50%");
      stage.style.setProperty("--look-origin-y", "48%");
      for (var i = 0; i < layers.length; i++) {
        layers[i].el.style.setProperty("--layer-x", "0px");
        layers[i].el.style.setProperty("--layer-y", "0px");
        layers[i].el.style.setProperty("--layer-roll", "0deg");
      }
    }

    /* ---- reduced motion gate ------------------------------- */
    function applyMotionMode() {
      fit(); // recompute scale (zoom drops to 1 under reduced motion)
      if (reduceMQ.matches) {
        stopLoop();
        hideMotionPrompt();
        hideRecenterControl();
        window.removeEventListener("mousemove", onWindowMove);
        root.removeEventListener("mouseleave", onLeave);
      } else {
        if (!isMobileCameraMode()) {
          hideMotionPrompt();
          hideRecenterControl();
          // listen on window so the canvas tracks the cursor over ANY
          // child (boxes, counter, layers). Gated to the hero bounds.
          window.addEventListener("mousemove", onWindowMove, { passive: true });
          root.addEventListener("mouseleave", onLeave);
        } else {
          window.removeEventListener("mousemove", onWindowMove);
          root.removeEventListener("mouseleave", onLeave);
          armGyro();
          enableSwipeLook();
          showRecenterControl();
          showMotionHint();
        }
        startLoop();
      }
    }

    // gate window-level moves to the hero's bounds (no global hijack)
    function onWindowMove(e) {
      var rect = getPointerBounds();
      if (
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top  && e.clientY <= rect.bottom
      ) {
        onMove(e);
      } else if (active) {
        onLeave();
      }
    }

    /* ---- click-only drawer reveal and delayed navigation ---- */
    var NAVIGATION_DELAY_MS = 1000;
    var SHINE_MS = 500;
    var boxes = root.querySelectorAll(".tcgv__box");
    var navigationTimer = null;
    var navigationLocked = false;

    function isModifiedClick(event) {
      return event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
        (typeof event.button === "number" && event.button !== 0);
    }

    function activateBox(box, event) {
      if (event.defaultPrevented || isModifiedClick(event)) return;
      var href = box.getAttribute("href");
      if (!href) return;

      event.preventDefault();
      if (navigationLocked) return;

      navigationLocked = true;
      boxes.forEach(function (item) { item.__tcgvClose(); });
      box.__tcgvOpen();
      navigationTimer = setTimeout(function () {
        window.location.href = href;
      }, NAVIGATION_DELAY_MS);
    }

    function resetBoxNavigation() {
      clearTimeout(navigationTimer);
      navigationTimer = null;
      navigationLocked = false;
      boxes.forEach(function (box) { box.__tcgvClose(); });
    }

    boxes.forEach(function (box) {
      var shineTimer = null;
      var shineRaf = null;

      function flashShine(delay) {
        if (reduceMQ.matches) return;
        clearTimeout(shineTimer);
        if (shineRaf) cancelAnimationFrame(shineRaf);
        shineTimer = setTimeout(function () {
          box.classList.remove("is-shining");
          shineRaf = requestAnimationFrame(function () {
            shineRaf = null;
            if (!box.classList.contains("is-open")) return;
            box.classList.add("is-shining");
            clearTimeout(shineTimer);
            shineTimer = setTimeout(function () {
              box.classList.remove("is-shining");
            }, SHINE_MS + 60);
          });
        }, delay || 0);
      }

      function open() {
        box.classList.add("is-open");
        flashShine(60);
      }
      function close() {
        clearTimeout(shineTimer);
        if (shineRaf) { cancelAnimationFrame(shineRaf); shineRaf = null; }
        box.classList.remove("is-settled");
        box.classList.remove("is-shining");
        box.classList.remove("is-open");
      }

      box.__tcgvOpen = open;
      box.__tcgvClose = close;
      box.addEventListener("click", function (event) {
        activateBox(box, event);
      });
    });

    window.addEventListener("pageshow", resetBoxNavigation);

    /* ---- flat click hit-layer ------------------------------------------
       The boxes live inside a 3D (preserve-3d + perspective) chain, where
       the browser's pointer hit-testing can land on a transparent ancestor
       instead of the box, so :hover/mouseenter on the box itself is
       unreliable. getBoundingClientRect(), however, DOES report each box's
       correct projected screen quad. So we lay a set of plain 2D zones in
       screen space (outside the 3D context) and keep them aligned to each
       box's projected rect while the camera moves. */
    var hitLayer = document.createElement("div");
    hitLayer.className = "tcgv__hitlayer";
    hitLayer.style.cssText =
      "position:absolute;inset:0;z-index:60;pointer-events:none;";
    var zones = [];
    var hitZonesEnabled = null;

    function setHitZonesEnabled(enabled) {
      if (hitZonesEnabled === enabled) return;
      hitZonesEnabled = enabled;
      for (var i = 0; i < zones.length; i++) {
        zones[i].z.style.pointerEvents = enabled ? "auto" : "none";
      }
    }

    boxes.forEach(function (box) {
      var z = document.createElement("a");
      var href = box.getAttribute("href");
      if (href) z.setAttribute("href", href);
      z.setAttribute("tabindex", "-1");
      z.setAttribute("aria-hidden", "true");
      z.style.cssText = "position:absolute;pointer-events:auto;cursor:pointer;";
      z.addEventListener("click", function (event) {
        activateBox(box, event);
      });
      hitLayer.appendChild(z);
      zones.push({ box: box, z: z, rect: null });
    });
    root.appendChild(hitLayer);

    function syncHitZones(force) {
      var mobileMode = isMobileCameraMode();
      setHitZonesEnabled(!mobileMode);
      if (mobileMode) return;
      var now = Date.now();
      if (!force && now - lastHitZoneSync < 33) return;
      lastHitZoneSync = now;
      var base = root.getBoundingClientRect();
      for (var i = 0; i < zones.length; i++) {
        var r = zones[i].box.getBoundingClientRect();
        zones[i].rect = r;
        var s = zones[i].z.style;
        s.left = (r.left - base.left) + "px";
        s.top = (r.top - base.top) + "px";
        s.width = r.width + "px";
        s.height = r.height + "px";
      }
    }
    syncHitZones();
    root.__tcgvSyncHits = syncHitZones;

    /* ---- wire up ------------------------------------------- */
    installPageZoomGuard();
    centerCamera(true);
    applyMotionMode();
    window.addEventListener("resize", fit, { passive: true });
    if (window.ResizeObserver) {
      root.__tcgvResizeObserver = new window.ResizeObserver(fit);
      root.__tcgvResizeObserver.observe(viewport);
    }
    function recenterForOrientation() {
      centerCamera(true);
      clearTimeout(orientationReplayTimer);
      orientationReplayTimer = setTimeout(function () {
        fit();
        showMotionHint();
      }, 140);
    }
    window.addEventListener("orientationchange", recenterForOrientation, { passive: true });
    if (window.screen && window.screen.orientation && window.screen.orientation.addEventListener) {
      window.screen.orientation.addEventListener("change", recenterForOrientation, { passive: true });
    }
    if (reduceMQ.addEventListener) reduceMQ.addEventListener("change", applyMotionMode);
    if (coarseMQ.addEventListener) coarseMQ.addEventListener("change", applyMotionMode);
    if (mobileViewMQ.addEventListener) mobileViewMQ.addEventListener("change", applyMotionMode);
    else if (mobileViewMQ.addListener) mobileViewMQ.addListener(applyMotionMode);
  }

  function boot(scope) {
    (scope || document).querySelectorAll(".tcgv").forEach(initVault);
  }
  window.__tcgvVaultBoot = boot;
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", function () { boot(document); });
  else boot(document);
  document.addEventListener("shopify:section:load", function (event) {
    boot(event.target);
  });
  document.addEventListener("shopify:section:select", function (event) {
    var root = event.target && event.target.querySelector(".tcgv");
    if (root && root.scrollIntoView) root.scrollIntoView({ block: "start" });
  });
})();

/* ============================================================
   LOWKEY — Navigation, full-screen mobile menu, search,
   drop countdown, and scroll-reveal.  Self-contained; runs
   independently of the parallax hero above.
   ============================================================ */
(function () {
  "use strict";

  if (window.__tcgvChromeBoot) {
    window.__tcgvChromeBoot(document);
    return;
  }

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

  function ready(fn) {
    if (document.readyState === "loading")
      document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }

  function initChrome(scope) {
    scope = scope || document;

    /* ---- nav: frost solid after a little scroll ------------ */
    var nav = scope.querySelector("[data-lk-nav]");
    if (nav && !nav.__lkNavInit) {
      nav.__lkNavInit = true;
      var navRaf = null;
      var navScrolled = null;
      var onScroll = function () {
        if (navRaf) return;
        navRaf = requestAnimationFrame(function () {
          navRaf = null;
          var nextScrolled = window.scrollY > 8;
          if (navScrolled === nextScrolled) return;
          navScrolled = nextScrolled;
          nav.classList.toggle("is-scrolled", nextScrolled);
        });
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    /* ---- search toggle ------------------------------------- */
    var searchBtn = scope.querySelector("[data-search-toggle]");
    var searchPanel = scope.querySelector("[data-search-panel]");
    if (searchBtn && searchPanel && !searchBtn.__lkSearchInit) {
      searchBtn.__lkSearchInit = true;
      var searchInput = searchPanel.querySelector('input[name="q"], input');
      var toggleSearch = function (force) {
        var open = force != null ? force : !searchPanel.classList.contains("is-open");
        searchPanel.classList.toggle("is-open", open);
        searchPanel.setAttribute("aria-hidden", open ? "false" : "true");
        searchBtn.setAttribute("aria-expanded", open ? "true" : "false");
        if (open && searchInput) setTimeout(function () { searchInput.focus(); }, 60);
      };
      searchBtn.addEventListener("click", function () { toggleSearch(); });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && searchPanel.classList.contains("is-open")) toggleSearch(false);
      });
      document.addEventListener("click", function (e) {
        if (!searchPanel.classList.contains("is-open")) return;
        if (searchPanel.contains(e.target) || searchBtn.contains(e.target)) return;
        toggleSearch(false);
      });
    }

    /* ---- full-screen mobile menu --------------------------- */
    var menu = scope.querySelector("[data-lk-menu]");
    var openBtn = scope.querySelector("[data-menu-open]");
    if (menu && openBtn && !menu.__lkMenuInit) {
      menu.__lkMenuInit = true;
      var closeEls = menu.querySelectorAll("[data-menu-close]");
      var scrollY = 0;
      var lastFocus = null;

      var focusables = function () {
        return menu.querySelectorAll(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
        );
      };

      var openMenu = function () {
        lastFocus = document.activeElement;
        menu.hidden = false;
        // force reflow so the open transition runs
        void menu.offsetWidth;
        menu.classList.add("is-open");
        openBtn.setAttribute("aria-expanded", "true");
        scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = -scrollY + "px";
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
        var first = focusables()[0];
        if (first) setTimeout(function () { first.focus(); }, 80);
      };

      var closeMenu = function () {
        menu.classList.remove("is-open");
        openBtn.setAttribute("aria-expanded", "false");
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
        var done = function () {
          menu.hidden = true;
          menu.removeEventListener("transitionend", done);
        };
        if (reduce.matches) { menu.hidden = true; }
        else menu.addEventListener("transitionend", done);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      };

      openBtn.addEventListener("click", openMenu);
      closeEls.forEach(function (el) { el.addEventListener("click", closeMenu); });

      document.addEventListener("keydown", function (e) {
        if (!menu.classList.contains("is-open")) return;
        if (e.key === "Escape") { closeMenu(); return; }
        if (e.key === "Tab") {
          var f = focusables();
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault(); last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault(); first.focus();
          }
        }
      });

      // close after tapping a category (let navigation proceed)
      menu.querySelectorAll(".lk-tile, .lk-menu__more a").forEach(function (a) {
        a.addEventListener("click", function () { closeMenu(); });
      });
    }

    var legacyMobileWrap = scope.querySelector(".tcgv__mobile");
    var legacyMobileEnabled = legacyMobileWrap && window.getComputedStyle(legacyMobileWrap).display !== "none";
    if (legacyMobileEnabled) {
    /* ---- drop countdown (next 6pm local) ------------------- */
    var counters = document.querySelectorAll("[data-countdown]");
    if (counters.length && !window.__tcgvCountdownInit) {
      window.__tcgvCountdownInit = true;
      var pad = function (n) { return (n < 10 ? "0" : "") + n; };
      var target = function () {
        var now = new Date();
        var t = new Date(now);
        t.setHours(18, 0, 0, 0);
        if (t <= now) t.setDate(t.getDate() + 1);
        return t;
      };
      var until = target();
      var tick = function () {
        var diff = until - new Date();
        if (diff <= 0) { until = target(); diff = until - new Date(); }
        var h = Math.floor(diff / 3.6e6);
        var m = Math.floor((diff % 3.6e6) / 6e4);
        var s = Math.floor((diff % 6e4) / 1000);
        var txt = pad(h) + ":" + pad(m) + ":" + pad(s);
        counters.forEach(function (c) { c.textContent = txt; });
      };
      tick();
      setInterval(tick, 1000);
    }

    /* ---- scroll-reveal for the shop-by-game tiles ---------- */
    var mobileWrap = scope.querySelector(".tcgv__mobile");
    var tiles = scope.querySelectorAll(".tcgv__mobile .tcgv__tile");
    if (tiles.length && mobileWrap && !mobileWrap.__lkRevealInit) {
      mobileWrap.__lkRevealInit = true;
      var revealAll = function () {
        tiles.forEach(function (t) { t.classList.add("is-inview"); });
      };
      if (reduce.matches || !("IntersectionObserver" in window) || !mobileWrap) {
        // leave tiles visible (no js-reveal class) — nothing to do
      } else {
        mobileWrap.classList.add("js-reveal");   // now tiles start hidden
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) {
              en.target.classList.add("is-inview");
              io.unobserve(en.target);
            }
          });
        }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
        tiles.forEach(function (t) { io.observe(t); });
        // safety net: never let a tile stay invisible
        setTimeout(revealAll, 1600);
      }

      // playful pop before navigating (mobile grid)
      tiles.forEach(function (tile) {
        if (tile.__lkTilePopInit) return;
        tile.__lkTilePopInit = true;
        tile.addEventListener("click", function (e) {
          if (reduce.matches || tile.dataset.popped) return;
          e.preventDefault();
          tile.classList.add("is-pop");
          tile.dataset.popped = "1";
          setTimeout(function () { window.location.href = tile.href; }, 320);
        });
      });
    }
    }
  }

  window.__tcgvChromeBoot = initChrome;
  ready(function () { initChrome(document); });
  document.addEventListener("shopify:section:load", function (event) {
    initChrome(event.target);
  });
})();
